import type { AppThunk } from '@doda/common/src/redux/store';
import { adobeAnalyticsScriptsSelector } from '@doda/common/src/redux/features/analytics';
import {
  sc,
  t,
  tlReadyExecute,
} from '@doda/common/src/utils/adobeAnalyticsUtil';
import { dataLayer } from '@doda/common/src/utils/googleTagManagerUtil';
import { cassifyListAsLMS } from '@doda/common/src/utils/convArrayUtil';
import type { RecommendTypes } from '@doda/common/src/interfaces/api/recommendPanel';
import type { AppQueryParams } from '@doda/common/src/constants/queryParams';
import {
  HA_SEARCH_ITEMS,
  HA_MASTER_ITEMS_LOWER,
  HA_MASTER_ITEMS_UPPER,
} from '@doda/common/src/constants/searchItems';
import { RANGE_LIST } from '@doda/common/src/constants/searchConditionModal';
import { JOB_COUNT_PER_PAGE } from 'constants/jobList';

/**
 * PV計測(AA)
 *
 * @param {Object} params - パラメータオブジェクト
 * @param {AppQueryParams} params.query - クエリパラメータ
 * @param {number} params.resultCount - 結果数
 * @param {number} params.primeCount - 特定の条件の結果数
 */
export function measureImpressionMatchJobListAA({
  query,
  resultCount,
  primeCount,
}: {
  query: AppQueryParams;
  resultCount: number;
  primeCount: number;
}) {
  const {
    lList: ocL,
    mList: ocM,
    sList: ocS,
  } = cassifyListAsLMS(query.oc ?? []);
  const { lList: indL, sList: indS } = cassifyListAsLMS(query.ind ?? []);

  const events = ['event195'];
  if (primeCount) {
    // 動画求人がある
    events.push('event97');
    if (primeCount === resultCount) {
      events.push('event231');
    }
  }

  sc({
    channel: 'PC_求人一覧(希望条件)',
    pageName: 'PC_求人一覧(希望条件)',
    linkTrackVars: primeCount
      ? 'pageName,channel,prop6,prop7,prop9,prop10,prop11,prop12,prop13,prop14,prop15,prop16,prop17,prop20,prop48,prop50,prop51,prop52,prop53,prop55,eVar56,eVar119,events'
      : 'pageName,channel,prop6,prop7,prop9,prop10,prop11,prop12,prop13,prop14,prop15,prop16,prop17,prop20,prop48,prop50,prop51,prop52,prop53,prop55,events',
    prop6: query.ci ? query.ci.join(',') : '',
    prop7: query.st ? query.st.join(',') : '',
    prop9: ocL ? ocL.join(',') : undefined,
    prop10: ocM ? ocM.join(',') : undefined,
    prop11: ocS ? ocS.join(',') : undefined,
    prop12: query.ar ? query.ar.join(',') : '',
    prop13: query.pr ? query.pr.join(',') : '',
    prop14: query.k ? `\u691C\u7D22:${query.k}` : '',
    prop15: query.op ? query.op.join(',') : '',
    prop16: indL ? indL.join(',') : undefined,
    prop17: indS ? indS.join(',') : undefined,
    prop20: query.op
      ? query.op
          .filter((param) => ['17', '18', '19'].some((el) => el === param))
          .join(',')
      : '',
    prop48: query.rt ? query.rt.join(',') : '',
    prop50: query.ha
      ? HA_SEARCH_ITEMS.find(
          (item) => item.id === (query.ha !== undefined ? query.ha[0] : '0')
        )?.name
      : '',
    prop51: query.ha
      ? HA_SEARCH_ITEMS.find(
          (item) => item.id === (query.ha !== undefined ? query.ha[1] : '0')
        )?.name
      : '',
    prop52: query.kwc
      ? RANGE_LIST.find((range) => query.kwc === range.value)?.label
      : '',
    prop53: (() => {
      if (query.tp === '2') return '\u76F4\u63A5\u5FDC\u52DF';
      if (query.tp === '0') return '\u8EE2\u8077\u652F\u63F4';
      return '\u5168\u6C42\u4EBA';
    })(),
    eVar56: primeCount ? String(primeCount) : undefined,
    eVar119: primeCount ? 'prime' : undefined,
    prop55: `\u30DA\u30FC\u30B8\u5185\u8868\u793A\u4EF6\u6570:${JOB_COUNT_PER_PAGE}`,
    linkTrackEvents: events.join(','),
    events: events.join(','),
  });
}

/**
 * PV計測(GA)
 *
 * @param {AppQueryParams} query - クエリパラメータ
 */
export function measureImpressionMatchJobListGA(query: AppQueryParams): void {
  const {
    lList: ocL,
    mList: ocM,
    sList: ocS,
  } = cassifyListAsLMS(query.oc ?? []);
  const { lList: indL, sList: indS } = cassifyListAsLMS(query.ind ?? []);

  const JOB_OFFER_KIND = { '0': '転職支援', '1': '全求人', '2': '直接応募' };
  const includedKeywordCategory = {
    '1': 'すべて',
    '2': '社名',
    '3': '職種名',
    '4': '勤務地',
    '9': 'スキル',
  };

  const salaryLowerId = query.ha
    ? HA_MASTER_ITEMS_LOWER.find(
        (item) => item.id === (query.ha !== undefined ? query.ha[0] : '0')
      )?.masterId
    : '';
  const salaryUpperId = query.ha
    ? HA_MASTER_ITEMS_UPPER.find(
        (item) => item.id === (query.ha !== undefined ? query.ha[1] : '0')
      )?.masterId
    : '';
  const optionsIdList: string[] = [];
  if (query.op) {
    query.op.forEach((item) => {
      optionsIdList.push(item);
    });
  }
  if (typeof salaryLowerId === 'string' && salaryLowerId !== '') {
    optionsIdList.push(salaryLowerId);
  }
  if (typeof salaryUpperId === 'string' && salaryUpperId !== '') {
    optionsIdList.push(salaryUpperId);
  }

  dataLayer({
    PageName: 'PC_求人一覧(希望条件)',
    Channel: 'PC_求人一覧(希望条件)',
    BizLargeClass: ocL ? ocL.join(',') : '',
    BizMiddleClass: ocM ? ocM.join(',') : '',
    BizSmallClass: ocS ? ocS.join(',') : '',
    WorkArea: query.ar ? query.ar.join(',') : '',
    WorkPrefecture: query.pr ? query.pr.join(',') : '',
    FreeWordSearch: query.k ? `検索:${query.k}` : '',
    SearchOption: optionsIdList ? optionsIdList.join(',') : '',
    IndLargeClass: indL ? indL.join(',') : '',
    IndSmallClass: indS ? indS.join(',') : '',
    EmpPattern: query.op
      ? query.op
          .filter((param) => ['17', '18', '19'].some((el) => el === param))
          .join(',')
      : '',
    WishLowSalary: salaryLowerId,
    WishUppSalary: salaryUpperId,
    KeywordSearchSeg: query.kwc ? includedKeywordCategory[query.kwc[0]] : '',
    JobListTab: query.tp ? JOB_OFFER_KIND[query.tp[0]] : '全求人',
  });
}

/**
 * 希望条件設定ボタンクリック計測
 *
 * @param dispatch
 * @param getState
 * @returns {AppThunk<void>} 計測用のThunk関数
 */
export const measureClickDesiredConditionSetting: AppThunk<void> = (
  dispatch,
  getState
) => {
  const isAdobeScript = adobeAnalyticsScriptsSelector(getState());
  tlReadyExecute(isAdobeScript, dispatch, 'jobRequirementsList_config', {
    linkTrackVars: 'prop1,prop23,prop69,eVar23,eVar78',
    linkTrackEvents:
      'event5,event8,event70,event71,event72,event73,event74,event81,event82,event135,event137',
    prop23: 'jobRequirementsList_config',
    eVar23: 'jobRequirementsList_config',
    events: 'prop23,eVar23',
  });
  dataLayer({
    event: 'setEffect',
    click_Label: 'prop23,eVar23',
    click_Action: 'jobRequirementsList_config',
    click_Category: 'jobRequirementsList_config',
  });
};

/**
 * 求人カード気になる計測
 *
 * @param {string} jid - 求人ID
 * @returns {AppThunk<void>} 計測用のThunk関数
 */
export function measureBookmarkJobCard(jid: string): AppThunk<void> {
  return (dispatch, getState) => {
    dataLayer({
      JobId: jid,
      event: 'bookmark_click',
    });
    const isAdobeScript = adobeAnalyticsScriptsSelector(getState());
    tlReadyExecute(isAdobeScript, dispatch, 'kininaruClick_MatchJobList', {
      linkTrackVars:
        'channel,events,products,prop1,prop23,prop32,prop69,eVar23,eVar78',
      linkTrackEvents: 'event120',
      channel: 'PC_求人一覧(希望条件)',
      products: `;${jid}`,
      prop23: 'kininaru_Click',
      prop32: jid,
      eVar23: 'kininaru_Click',
      events: 'event120',
    });
  };
}

type JobOfferType = 'Direct' | 'Agent';
const map: Record<JobOfferType, Record<'linkName' | 'events', string>> = {
  Direct: {
    linkName: 'Chokusetsu Ichiran',
    events: 'event8',
  },
  Agent: {
    linkName: 'Shokai Ichiran',
    events: 'event5',
  },
};

/**
 * 求人カード応募ボタンクリック計測
 *
 * @param {JobOfferType} type - 応募の種類
 * @returns {AppThunk<void>} 計測用のThunk関数
 */
export function measureApplyJob(type: JobOfferType): AppThunk<void> {
  return (dispatch, getState) => {
    const isAdobeScript = adobeAnalyticsScriptsSelector(getState());
    tlReadyExecute(isAdobeScript, dispatch, map[type].linkName, {
      linkTrackVars: 'prop1,prop23,prop69,eVar23,eVar78',
      linkTrackEvents:
        'event5,event8,event70,event71,event72,event73,event74,event81,event82,event135,event137',
      prop23: '求人一覧ボタン',
      eVar23: '求人一覧ボタン',
      events: map[type].events,
    });
    dataLayer({
      event: 'setEffect',
      click_Label: map[type].events,
      click_Action: '求人一覧ボタン',
      click_Category: map[type].linkName,
    });
  };
}

/**
 * レコメンドカードクリック計測
 *
 * @param {Object} params - パラメータオブジェクト
 * @param {RecommendTypes} params.recommendType - レコメンドの種類
 * @param {string} params.effectName - 効果名
 * @returns {AppThunk<void>} 計測用のThunk関数
 */
export function measureClickRecommendCard({
  recommendType,
  effectName,
}: {
  recommendType: RecommendTypes;
  effectName: string;
}): AppThunk<void> {
  return (dispatch, getState) => {
    const isAdobeScript = adobeAnalyticsScriptsSelector(getState());
    tlReadyExecute(isAdobeScript, dispatch, 'siteRecommendTracking', {
      linkTrackVars: 'prop23,eVar23,evar103,evar104,evar105,evar187',
      eVar103: recommendType,
      eVar104: effectName,
    });
    dataLayer({
      siteRecommendLogic: recommendType,
      siteRecommendTrackParam: effectName,
      event: 'siteRecommendTracking',
    });
  };
}

/**
 * レコメンドカード気になる計測
 *
 * @param {string} jid - 求人ID
 * @returns {AppThunk<void>} 計測用のThunk関数
 */
export function measureBookmarkRecommendCard(jid: string): AppThunk<void> {
  return (dispatch, getState) => {
    dataLayer({
      JobId: jid,
      event: 'bookmark_click',
    });
    const isAdobeScript = adobeAnalyticsScriptsSelector(getState());
    tlReadyExecute(isAdobeScript, dispatch, 'kininaruClick_MatchJobList_rec', {
      linkTrackVars:
        'channel,events,products,prop1,prop23,prop32,prop69,eVar23,eVar78',
      linkTrackEvents: 'event120',
      channel: 'PC_求人一覧(希望条件)',
      products: `;${jid}`,
      prop23: 'kininaru_Click',
      prop32: jid,
      eVar23: 'kininaru_Click',
      events: 'event120',
    });
  };
}

/**
 * 希望条件モーダルPV計測
 */
export function measurePageViewDesiredConditionSetting() {
  const pageName = 'PC_希望条件再ヒアリング画面';
  const channel = '希望条件再ヒアリング画面';
  // AA計測
  sc({
    channel,
    pageName,
  });
  t();
  // GA計測
  dataLayer({
    Channel: channel,
    PageName: pageName,
    event: 'setEffectPv',
  });
}
